import { yupResolver } from '@hookform/resolvers/yup';
import Grid from '@material-ui/core/Grid';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';
import Box from '@mui/material/Box';
import { isValid } from 'clients/Clients';
import { createCommentNotLogin } from 'clients/WebServiceClient';
import clsx from 'clsx';
import SelectBase from 'components-v2/atoms/SelectBase';
import TextFieldBase from 'components-v2/atoms/TextFieldBase';
import NotificationModal from 'components-v2/mocules/NotificationModal';
import { ENUM_SCOPE_LABEL } from 'constants/Enums';
import { ICON_REGISTER_DROPDOWN } from 'constants/Icons';
import { QA_LOCATION_ICON, QA_STORE_ICON } from 'constants/Images';
import { useProvinces } from 'hooks/useProvinces';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { Controller, get, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import NotifyUtils from 'utils/NotifyUtils';
import { QAValues, schemaNotLogin } from '../constants';
import styles from './styles.module.css';

interface Props {
  isOpen: boolean;
  targetCode: string;
  onClose: () => void;
}

const UnregisteredQuestionForm = (props: Props) => {
  const { targetCode, isOpen, onClose } = props;
  const [provinces, setProvinces] = useState<any[]>([]);
  const {
    control: controlNotLogin,
    handleSubmit,
    formState,
    reset,
  } = useForm({
    resolver: yupResolver(schemaNotLogin),
  });
  const { getProvinceOptions } = useProvinces();

  const fetchProvinces = async () => {
    try {
      const provincesRs: any[] = await getProvinceOptions();
      setProvinces(provincesRs);
    } catch (error) {
      NotifyUtils.error('Lấy danh sách tỉnh thành không thành công');
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: QAValues) => {
    const { businessType, question, numberPhone, fullName, province } = data;

    const params = {
      content: question,
      targetCode,
      isBasic: true,
      fullname: fullName,
      provinceCode: province.value,
      scope: businessType?.key,
      phone: numberPhone,
    };

    const rs = await createCommentNotLogin(params);

    if (!isValid(rs)) {
      toast.error(rs.message || 'Có lỗi xảy ra, vui lòng thử lại');
      return;
    }

    reset();
    onClose();
    toast.success('Cảm ơn bạn đã gửi ý kiến cho thuocsi.vn. Ý kiến của bạn đang chờ duyệt.', { autoClose: 3000 });
  };

  useEffect(() => {
    if (!isOpen) return;
    if (provinces.length > 0) return;

    fetchProvinces();
  }, [isOpen]);

  return (
    <NotificationModal
      classNames={styles.loginFormModal}
      title="Ý kiến về sản phẩm"
      maxWidth="sm"
      cancelText="Hủy"
      submitText="Gửi"
      isOpen={isOpen}
      isDisableSubmit={formState.isSubmitting}
      onClose={handleClose}
      onCancel={handleClose}
      onSubmit={handleSubmit(onSubmit)}
    >
      <form className={styles.loginForm}>
        <Grid container spacing={1}>
          <Grid item md={6}>
            <Controller
              name="fullName"
              control={controlNotLogin}
              render={({ field }) => {
                const error = get(formState.errors, field.name);
                return <TextFieldBase {...field} placeholder="* Họ và tên (Bắt buộc)" error={!!error?.message} helperText={error?.message} />;
              }}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="numberPhone"
              control={controlNotLogin}
              render={({ field }) => {
                const error = get(formState.errors, field.name);
                return <TextFieldBase {...field} placeholder="* Số điện thoại (Bắt buộc)" error={!!error?.message} helperText={error?.message} />;
              }}
            />
          </Grid>
          <Grid item md={12}>
            <Controller
              name="businessType"
              control={controlNotLogin}
              render={({ field }) => {
                const error = get(formState.errors, field.name);
                return (
                  <SelectBase
                    {...field}
                    renderValue={(selected: any) => {
                      if (!selected?.value) {
                        return <div className={styles.placeholder}>Bạn là</div>;
                      }
                      return selected?.value;
                    }}
                    error={error}
                    iconStart={<Image src={QA_STORE_ICON} width={24} height={24} loading="lazy" alt="select icon" />}
                    iconEnd={<ICON_REGISTER_DROPDOWN />}
                  >
                    {Object.entries(ENUM_SCOPE_LABEL).map((arr) => {
                      const key = arr[0];
                      const value = arr[1];
                      return (
                        <MenuItem className={styles.menuItem} key={key} value={{ key, value }}>
                          <Radio checked={field?.value?.key === key} />
                          <ListItemText primary={value} />
                        </MenuItem>
                      );
                    })}
                  </SelectBase>
                );
              }}
            />
          </Grid>
          <Grid item md={12}>
            <Controller
              name="province"
              control={controlNotLogin}
              render={({ field }) => {
                const error = get(formState.errors, field.name);
                return (
                  <SelectBase
                    {...field}
                    renderValue={(selected: any) => {
                      if (!selected?.label) {
                        return <div className={styles.placeholder}>Tỉnh/Thành phố *</div>;
                      }
                      return selected?.label;
                    }}
                    placeholder="Tỉnh/Thành phố"
                    error={error}
                    iconStart={<Image src={QA_LOCATION_ICON} width={24} height={24} loading="lazy" alt="select icon" />}
                    iconEnd={<ICON_REGISTER_DROPDOWN />}
                  >
                    {provinces.map((province) => (
                      <MenuItem key={province.value} value={province as any}>
                        <ListItemText primary={province.label} />
                      </MenuItem>
                    ))}
                  </SelectBase>
                );
              }}
            />
          </Grid>
          <Grid item md={12}>
            <Controller
              name="question"
              control={controlNotLogin}
              render={({ field }) => {
                const error = get(formState.errors, field.name);
                return (
                  <Box className={styles.formTextarea}>
                    <TextareaAutosize
                      className={clsx(styles.input, { [styles.error]: !!error })}
                      minRows={3}
                      maxRows={3}
                      maxLength={200}
                      placeholder="* Vui lòng nhập nội dung câu hỏi bằng tiếng Việt có dấu, tối thiểu 3 ký tự (Bắt buộc)"
                      {...field}
                    />
                    {error ? <span>{error.message}</span> : <div className={styles.textareaCount}>{field.value ? field.value.length : 0}/ 200</div>}
                  </Box>
                );
              }}
            />
          </Grid>
        </Grid>
      </form>
    </NotificationModal>
  );
};

export default UnregisteredQuestionForm;
